import React, { useCallback, useMemo, useState } from 'react';

import { faEdit, faExchangeAlt, faRecycle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { Button } from '@mui/material';
import cs from 'classnames';
import dayjs from 'dayjs';
import { useUnit } from 'effector-react';
import { MRT_ColumnDef, MRT_SortingState } from 'material-react-table';
import { useNavigate } from 'react-router-dom';

import { DeleteDialog } from './components/DeleteDialog';
import { InvestBoxDialog } from './components/InvestBoxDialog';
import { useInvestBoxes } from './hooks';
import { ProtectedComponent } from '../../../components/ProtectedComponent';
import { TableGridLocal } from '../../../components/TableGridLocal';
import { User$ } from '../../../effector/user';
import { AggregateInvestbox } from '../../../types/aggregateInvestbox';
import { UserRoles } from '../../../types/userRoles';
import { boolToString } from '../../../utils';
import { checkPermissions, UserAction } from '../../../utils/permissions';
import { PAGE_ADMINISTRATION, PAGE_ADMINISTRATION_INVESTBOXES } from '../../Routes';
import { VisibilityDialog } from './components/VisibilityDialog';

export const Investboxes: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [changeVisibilityCandidate, setChangeVisibilityCandidate] = useState<{
    strategyId: string;
    isOpen: boolean;
  } | null>(null);
  const [deleteCandidate, setDeleteCandidate] = useState<{
    strategyId: string;
    active: boolean;
  } | null>(null);

  const navigate = useNavigate();
  const user = useUnit(User$);

  const { data: investBoxes, refetch } = useInvestBoxes();

  const onEdit = useCallback(
    (val: string) => {
      const path = `${PAGE_ADMINISTRATION}/${PAGE_ADMINISTRATION_INVESTBOXES}/${val}`;
      navigate(path);
    },
    [navigate],
  );

  const handleAddInvestbox = useCallback(() => {
    setOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setDeleteCandidate(null);
    setChangeVisibilityCandidate(null);
  }, []);

  const handleUpdate = useCallback(() => {
    handleCloseDialog();
    refetch();
  }, [handleCloseDialog, refetch]);

  const isDeveloper = user?.role && user.role === UserRoles.Developer;

  const columns = useMemo<MRT_ColumnDef<AggregateInvestbox>[]>(() => {
    if (user) {
      const items: (MRT_ColumnDef<AggregateInvestbox> & { action: UserAction })[] = [
        {
          header: 'Название',
          accessorKey: 'title',
          enableColumnFilter: false,
          enableColumnOrdering: false,
          enableColumnFilterModes: false,
          enableColumnActions: false,
          action: UserAction.administrationInvestbox,
        },
        {
          header: 'Создана',
          accessorKey: 'creationDate',
          enableColumnFilter: false,
          enableColumnOrdering: false,
          enableColumnFilterModes: false,
          enableColumnActions: false,
          action: UserAction.administrationInvestbox,
          Cell: ({
            cell: {
              row: { original },
            },
          }) => {
            const timeFormatted = original?.creationDate ? dayjs(original.creationDate).format('DD.MM.YYYY HH:mm') : '';

            return <span>{timeFormatted}</span>;
          },
        },
        {
          header: 'Тариф',
          accessorKey: 'tariffName',
          enableColumnFilter: false,
          enableColumnOrdering: false,
          enableColumnFilterModes: false,
          enableColumnActions: false,
          action: UserAction.administrationInvestbox,
        },
        {
          header: 'Валюта',
          accessorKey: 'currency',
          enableColumnFilter: false,
          enableColumnOrdering: false,
          enableColumnFilterModes: false,
          enableColumnActions: false,
          action: UserAction.administrationInvestbox,
        },
        {
          header: 'Открыта',
          accessorFn: (originalRow) => boolToString(originalRow.isOpen),
          enableColumnFilter: false,
          enableColumnOrdering: false,
          enableColumnFilterModes: false,
          enableColumnActions: false,
          action: UserAction.administrationInvestbox,
          Cell: ({ cell }) => <span>{boolToString(cell?.row?.original?.isOpen)}</span>,
        },
        {
          header: '',
          accessorKey: 'edit',
          enableColumnFilter: false,
          enableColumnOrdering: false,
          enableColumnFilterModes: false,
          enableColumnActions: false,
          enableSorting: false,
          size: 40,
          action: UserAction.administrationInvestboxAction,
          Cell: ({
            cell: {
              row: { original },
            },
          }) => {
            return (
              <span className="pointer">
                <FontAwesomeIcon
                  icon={faEdit}
                  title="Редактировать"
                  className={cs({ 'fa-disabled': isDeveloper && !original.testMode })}
                  onClick={() => onEdit(original.id)}
                />
              </span>
            );
          },
        },
        {
          header: '',
          accessorKey: 'close',
          enableColumnFilter: false,
          enableColumnOrdering: false,
          enableColumnFilterModes: false,
          enableColumnActions: false,
          enableSorting: false,
          size: 40,
          action: UserAction.administrationInvestboxAction,
          Cell: ({
            cell: {
              row: { original },
            },
          }) => {
            return (
              <span className="pointer">
                <FontAwesomeIcon
                  icon={faExchangeAlt}
                  title="Открыть/закрыть"
                  className={cs({
                    fall: original.testMode || !isDeveloper,
                    'fa-disabled': isDeveloper && !original.testMode,
                  })}
                  onClick={() => {
                    setChangeVisibilityCandidate({
                      strategyId: original.id,
                      isOpen: original.isOpen,
                    });
                  }}
                />
              </span>
            );
          },
        },
        {
          header: '',
          accessorKey: 'remove',
          enableColumnFilter: false,
          enableColumnOrdering: false,
          enableColumnFilterModes: false,
          enableColumnActions: false,
          enableSorting: false,
          size: 40,
          action: UserAction.administrationInvestboxAction,
          Cell: ({
            cell: {
              row: { original },
            },
          }) => {
            return (
              <span className="pointer">
                <FontAwesomeIcon
                  icon={faRecycle}
                  title="Удалить/активировать"
                  className={cs({ 'fa-disabled': isDeveloper && !original.testMode })}
                  onClick={() => {
                    setDeleteCandidate({
                      strategyId: original.id,
                      active: original.active,
                    });
                  }}
                />
              </span>
            );
          },
        },
      ];

      return items.filter((item) => checkPermissions(user.role, item.action));
    }

    return [];
  }, [isDeveloper, onEdit, user]);

  const columnSorts = useMemo<MRT_SortingState>(
    () => [
      {
        desc: true,
        id: 'name',
      },
    ],
    [],
  );

  return (
    <>
      <ProtectedComponent userAction={UserAction.administrationDeveloper}>
        <div className="form__group">
          <br />
          <p>Для данного пользователя доступно редактирование только тестовых копилок.</p>
          <br />
        </div>
      </ProtectedComponent>

      <div className="form__group">
        <ProtectedComponent userAction={UserAction.administrationInvestboxAction}>
          <Button variant="contained" color="primary" startIcon={<AddRoundedIcon />} onClick={handleAddInvestbox}>
            Добавить
          </Button>
        </ProtectedComponent>
      </div>

      <TableGridLocal
        name="invet-boxes"
        columns={columns}
        columnSorts={columnSorts}
        data={investBoxes?.result.pageItems || []}
        totalRowCount={investBoxes?.result.total || 0}
      />

      {open && <InvestBoxDialog onClose={() => setOpen(false)} />}
      {changeVisibilityCandidate && (
        <VisibilityDialog
          strategyId={changeVisibilityCandidate.strategyId}
          isOpen={changeVisibilityCandidate.isOpen}
          onUpdate={handleUpdate}
          onClose={handleCloseDialog}
        />
      )}
      {deleteCandidate && (
        <DeleteDialog
          strategyId={deleteCandidate.strategyId}
          active={deleteCandidate.active}
          onUpdate={handleUpdate}
          onClose={handleCloseDialog}
        />
      )}
    </>
  );
};
